<template>
  <div class="site-plan-viewer">
    <div class="site-plan-shell">

      <!-- 상단 버튼 이미지 영역 -->
      <section class="section-card compact-card">
        <div class="section-head">
          <div class="section-head-text">
            <div class="section-title">현장 버튼 이미지</div>
            <div class="section-subtitle">
              버튼을 클릭하면 팝업으로 크게 확인할 수 있습니다.
            </div>
          </div>
        </div>

        <div class="button-gallery-section">
          <div v-if="visibleButtons.length" class="button-gallery-list">
            <button
              v-for="btn in visibleButtons"
              :key="btn.buttonNo"
              type="button"
              class="button-gallery-card"
              :class="{ active: isSelectedButton(btn) }"
              @click="openButtonModal(btn)"
            >
              <div
                class="button-gallery-top"
                :style="getButtonTextStyle(btn)"
              >
                <span class="button-gallery-text">
                  {{ btn.topText || '-' }}
                </span>
              </div>

              <div class="button-gallery-image-wrap">
                <img
                  v-if="btn.imageUrl"
                  :src="btn.imageUrl"
                  :alt="btn.topText || btn.bottomText || `button-${btn.buttonNo}`"
                  class="button-gallery-image"
                />
                <div v-else class="button-gallery-image-empty">
                  이미지 없음
                </div>
              </div>

              <div
                class="button-gallery-bottom"
                :style="getButtonTextStyle(btn)"
              >
                <span class="button-gallery-text">
                  {{ btn.bottomText || '-' }}
                </span>
              </div>
            </button>
          </div>

          <div v-else class="button-gallery-empty">
            표시할 버튼 이미지가 없습니다.
          </div>
        </div>
      </section>

      <!-- 현장 도면 영역 -->
      <section class="section-card plan-card">
        <div class="section-head">
          <div class="section-head-text">
            <div class="section-title">현장 도면</div>
            <div class="section-subtitle">
              현장 도면과 로거 아이콘 위치를 표시합니다.
            </div>
          </div>
        </div>

        <div class="viewer-stage-section">
          <div v-if="imageUrl" ref="planViewport" class="image-wrapper">
            <div
              class="plan-stage"
              :style="planStageStyle"
            >
              <img
                ref="planImage"
                :src="imageUrl"
                :alt="alt"
                class="plan-image"
                @load="onImageLoad"
              />

              <div
                v-for="logger in loggerStatusList"
                :key="logger.sensorLoggerSiteId || logger.sensorLoggerId"
                class="logger-marker"
                :class="{ active: activeTooltipId === getLoggerKey(logger) }"
                :style="getLoggerStyle(logger)"
                @mouseenter="openTooltip(logger)"
                @mouseleave="scheduleCloseTooltip"
              >
                <div class="logger-icon-wrap">
                  <img
                    :src="getLoggerIcon(logger)"
                    :alt="getLoggerStatus(logger)"
                    class="logger-icon"
                  />

                  <div
                    v-show="activeTooltipId === getLoggerKey(logger)"
                    class="logger-tooltip"
                    :class="getTooltipClass(logger)"
                    @mouseenter="cancelCloseTooltip"
                    @mouseleave="scheduleCloseTooltip"
                  >
                    <div class="tooltip-row">
                      <span class="tooltip-label">장비명 :</span>
                      <span class="tooltip-value">{{ logger.name || '-' }}</span>
                    </div>

                    <div class="tooltip-row">
                      <span class="tooltip-label">상태 :</span>
                      <span class="tooltip-value">{{ getLoggerStatus(logger) }}</span>
                    </div>

                    <div
                      v-if="hasMeaningfulEvent(logger)"
                      class="tooltip-row"
                    >
                      <span class="tooltip-label">발생시각 :</span>
                      <span class="tooltip-value">{{ formatDateTime(logger.occurredAt) }}</span>
                    </div>

                    <div
                      v-if="logger.currentValue !== null && logger.currentValue !== undefined"
                      class="tooltip-row"
                    >
                      <span class="tooltip-label">출력값 :</span>
                      <span class="tooltip-value">{{ formatValue(logger.currentValue) }}</span>
                    </div>

                    <div
                      v-if="logger.statusText"
                      class="tooltip-row"
                    >
                      <span class="tooltip-label">내용 :</span>
                      <span class="tooltip-value">{{ logger.statusText }}</span>
                    </div>

                    <div
                      v-if="hasMeaningfulEvent(logger)"
                      class="tooltip-search-guide"
                    >
                      <div class="tooltip-guide-title">이벤트 이력 검색용 정보</div>
                      <div class="tooltip-guide-line">
                        로거명: {{ logger.name || '-' }}
                      </div>
                      <div class="tooltip-guide-line">
                        이벤트유형: {{ getLoggerStatus(logger) }}
                      </div>
                      <div class="tooltip-guide-line">
                        발생시각: {{ formatDateTime(logger.occurredAt) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="placeholder">
            현장 도면이 없습니다
          </div>
        </div>
      </section>

      <!-- 하단 요약 차트 -->
      <section class="section-card compact-card">
        <div class="section-head">
          <div class="section-head-text">
            <div class="section-title">장비 요약 현황</div>
            <div class="section-subtitle">
              현장 기준 설치 현황과 통신 상태를 요약합니다.
            </div>
          </div>
        </div>

        <div class="plan-summary-section">
          <div class="summary-chart-card">
            <div class="summary-chart-header">
              <span class="summary-chart-index">1</span>
              <span class="summary-chart-title">전체 장비 설치 현황</span>
            </div>

            <div class="summary-chart-body">
              <div class="summary-chart-canvas-wrap">
                <div class="summary-chart-canvas">
                  <canvas ref="installChartCanvas"></canvas>
                </div>
              </div>

              <div class="summary-chart-meta">
                <div class="summary-meta-total">설치 {{ totalInstalled }} 대</div>

                <ul class="summary-legend-list">
                  <li v-for="item in installLegend" :key="item.label" class="summary-legend-item">
                    <span class="summary-legend-color" :style="{ backgroundColor: item.color }"></span>
                    <span class="summary-legend-label">{{ item.label }}</span>
                    <span class="summary-legend-value">{{ item.value }}/{{ totalInstalled }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="summary-chart-card">
            <div class="summary-chart-header">
              <span class="summary-chart-index">2</span>
              <span class="summary-chart-title">전체 장비 통신 상태</span>
            </div>

            <div class="summary-chart-body">
              <div class="summary-chart-canvas-wrap">
                <div class="summary-chart-canvas">
                  <canvas ref="communicationChartCanvas"></canvas>
                </div>
              </div>

              <div class="summary-chart-meta">
                <div class="summary-meta-total">설치 {{ totalInstalled }} 대</div>

                <ul class="summary-legend-list">
                  <li v-for="item in communicationLegend" :key="item.label" class="summary-legend-item">
                    <span class="summary-legend-color" :style="{ backgroundColor: item.color }"></span>
                    <span class="summary-legend-label">{{ item.label }}</span>
                    <span class="summary-legend-value">{{ item.value }}/{{ totalInstalled }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>

    <!-- 버튼 이미지 팝업 -->
    <div
      v-if="isButtonModalOpen"
      class="button-modal-backdrop"
      @click.self="closeButtonModal"
    >
      <div class="button-modal-panel">
        <div class="button-modal-topbar">
          <div class="button-modal-meta">
            <div class="button-modal-title">
              {{ activeButton?.topText || activeButton?.bottomText || '버튼 이미지 보기' }}
            </div>
            <div class="button-modal-count">
              {{ activeButtonIndex + 1 }} / {{ visibleButtons.length }}
            </div>
          </div>

          <button
            type="button"
            class="button-modal-close"
            @click="closeButtonModal"
          >
            닫기
          </button>
        </div>

        <div
          class="button-modal-body"
          :class="{ 'single-mode': isSingleButtonMode }"
        >
          <button
            v-if="!isSingleButtonMode"
            type="button"
            class="button-modal-nav left"
            @click="prevButton"
          >
            <i class="fa-solid fa-chevron-left"></i>
          </button>

          <div class="button-modal-content">
            <!-- 1개일 때: 슬라이드 말고 단일 표시 -->
            <template v-if="isSingleButtonMode">
              <template v-if="activeButton?.imageUrl">
                <div class="button-modal-single-panel">
                  <div
                    class="button-modal-single-top"
                    :style="getButtonTextStyle(activeButton)"
                  >
                    <span class="button-modal-single-text">
                      {{ activeButton?.topText || '-' }}
                    </span>
                  </div>

                  <div class="button-modal-single-image-stage">
                    <img
                      :src="activeButton.imageUrl"
                      :alt="activeButton?.topText || activeButton?.bottomText || 'button image'"
                      class="button-modal-single-image"
                    />
                  </div>

                  <div
                    class="button-modal-single-bottom"
                    :style="getButtonTextStyle(activeButton)"
                  >
                    <span class="button-modal-single-text">
                      {{ activeButton?.bottomText || '-' }}
                    </span>
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="button-modal-empty-wide-panel">
                  <div class="button-modal-empty-wide-header">
                    <div class="button-modal-empty-wide-title">
                      {{ activeButton?.topText || activeButton?.bottomText || '버튼 이미지' }}
                    </div>
                    <div class="button-modal-empty-wide-subtitle">
                      등록된 이미지가 없습니다.
                    </div>
                  </div>

                  <div class="button-modal-empty-wide-body">
                    <div class="button-modal-empty-wide-icon">
                      <i class="fa-regular fa-image"></i>
                    </div>

                    <div class="button-modal-empty-wide-message">
                      버튼 이미지는 아직 업로드되지 않았습니다.
                    </div>

                    <div class="button-modal-empty-wide-desc">
                      현재는 버튼 상·하단 문구만 등록된 상태입니다.
                    </div>

                    <div class="button-modal-empty-wide-meta">
                      <div class="button-modal-empty-wide-row">
                        <span class="meta-label">상단 문구</span>
                        <span
                          class="meta-value chip"
                          :style="getButtonTextStyle(activeButton)"
                        >
                          {{ activeButton?.topText || '-' }}
                        </span>
                      </div>

                      <div class="button-modal-empty-wide-row">
                        <span class="meta-label">하단 문구</span>
                        <span
                          class="meta-value chip"
                          :style="getButtonTextStyle(activeButton)"
                        >
                          {{ activeButton?.bottomText || '-' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </template>

            <!-- 2개 이상일 때만 기존 슬라이드 구조 -->
            <template v-else>
              <template v-if="activeButton?.imageUrl">
                <div class="button-modal-card">
                  <div
                    class="button-modal-card-top"
                    :style="getButtonTextStyle(activeButton)"
                  >
                    <span class="button-modal-card-text">
                      {{ activeButton?.topText || '-' }}
                    </span>
                  </div>

                  <div class="button-modal-image-stage">
                    <img
                      :src="activeButton.imageUrl"
                      :alt="activeButton?.topText || activeButton?.bottomText || 'button image'"
                      class="button-modal-image"
                    />
                  </div>

                  <div
                    class="button-modal-card-bottom"
                    :style="getButtonTextStyle(activeButton)"
                  >
                    <span class="button-modal-card-text">
                      {{ activeButton?.bottomText || '-' }}
                    </span>
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="button-modal-empty-panel">
                  <div class="button-modal-empty-header">
                    <div class="button-modal-empty-title-wrap">
                      <div class="button-modal-empty-title-main">
                        {{ activeButton?.topText || activeButton?.bottomText || '버튼 이미지' }}
                      </div>
                      <div class="button-modal-empty-title-sub">
                        등록된 이미지가 없습니다.
                      </div>
                    </div>
                  </div>

                  <div class="button-modal-empty-body">
                    <div class="button-modal-empty-icon">
                      <i class="fa-regular fa-image"></i>
                    </div>

                    <div class="button-modal-empty-message">
                      버튼 이미지는 아직 업로드되지 않았습니다.
                    </div>

                    <div class="button-modal-empty-desc">
                      현재는 버튼 상·하단 문구만 등록된 상태입니다.
                    </div>

                    <div class="button-modal-empty-meta">
                      <div class="button-modal-meta-row">
                        <span class="meta-label">상단 문구</span>
                        <span
                          class="meta-value chip"
                          :style="getButtonTextStyle(activeButton)"
                        >
                          {{ activeButton?.topText || '-' }}
                        </span>
                      </div>

                      <div class="button-modal-meta-row">
                        <span class="meta-label">하단 문구</span>
                        <span
                          class="meta-value chip"
                          :style="getButtonTextStyle(activeButton)"
                        >
                          {{ activeButton?.bottomText || '-' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </div>

          <button
            v-if="!isSingleButtonMode"
            type="button"
            class="button-modal-nav right"
            @click="nextButton"
          >
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>

        <div v-if="!isSingleButtonMode" class="button-modal-indicators">
          <button
            v-for="(btn, index) in visibleButtons"
            :key="btn.buttonNo"
            type="button"
            class="button-modal-dot"
            :class="{ active: index === activeButtonIndex }"
            @click="goToButton(index)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import iconDangerous from '@/assets/sensor/sensor-status-dangerous.png'
import iconDetect from '@/assets/sensor/sensor-status-detect.png'
import iconWarning from '@/assets/sensor/sensor-status-warning.png'
import iconNormal from '@/assets/sensor/sensor-status-normal.png'
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js'

Chart.register(
  ArcElement,
  Tooltip,
  Legend
)

export default {
  name: 'SitePlanViewer',

  props: {
    imageUrl: {
      type: String,
      default: ''
    },
    alt: {
      type: String,
      default: '현장 도면'
    },
    siteId: {
      type: [Number, String],
      default: null
    }
  },

  emits: ['button-click'],

  data() {
    return {
      loggerStatusList: [],
      imageNaturalWidth: 0,
      imageNaturalHeight: 0,
      activeTooltipId: null,
      tooltipCloseTimer: null,

      buttonItems: [],
      isButtonModalOpen: false,
      activeButtonIndex: 0,

      planRenderWidth: 0,
      planRenderHeight: 0,

      resizeObserver: null,

      installChart: null,
      communicationChart: null,

      fitRafId: null,
      chartRafId: null,

      installLegend: [
        { label: '구조물경사계', value: 0, color: '#4a78c9' },
        { label: '지표침하계', value: 0, color: '#f28c28' },
        { label: '레일변위계', value: 0, color: '#2bb3c0' }
      ],

      communicationLegend: [
        { label: '정상', value: 0, color: '#4a78c9' },
        { label: '통신 두절', value: 0, color: '#f28c28' }
      ],

      iconMap: {
        경보: iconDangerous,
        경고: iconWarning,
        감지: iconDetect,
        정상: iconNormal
      }
    }
  },

  computed: {
    visibleButtons() {
      return (this.buttonItems || [])
        .filter(item => item && item.useYn !== false)
        .sort((a, b) => {
          const aNo = Number(a.buttonNo || a.slotNo || 0)
          const bNo = Number(b.buttonNo || b.slotNo || 0)
          return aNo - bNo
        })
        .slice(0, 4)
    },

    activeButton() {
      if (!this.visibleButtons.length) return null
      return this.visibleButtons[this.activeButtonIndex] || null
    },

    isSingleButtonMode() {
      return this.visibleButtons.length <= 1
    },

    totalInstalled() {
      return this.installLegend.reduce((sum, item) => sum + Number(item.value || 0), 0)
    },

    planStageStyle() {
      if (!this.planRenderWidth || !this.planRenderHeight) {
        return {
          width: '100%',
          maxWidth: '100%'
        }
      }

      return {
        width: `${this.planRenderWidth}px`,
        height: `${this.planRenderHeight}px`
      }
    }
  },

  watch: {
    siteId: {
      immediate: true,
      async handler(value) {
        this.closeTooltip()
        this.closeButtonModal(false)

        if (value) {
          await Promise.all([
            this.fetchLoggerStatus(value),
            this.fetchButtons(value),
            this.fetchOperationSummary(value)
          ])
        } else {
          this.loggerStatusList = []
          this.buttonItems = []
          this.installLegend = [
            { label: '데이터 없음', value: 0, color: '#d1d5db' }
          ]

          this.communicationLegend = [
            { label: '정상', value: 0, color: '#4a78c9' },
            { label: '통신 두절', value: 0, color: '#f28c28' }
          ]
        }

        await this.$nextTick()

        this.safeRenderSummaryCharts()
        this.safeUpdatePlanFit()
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.onWindowResize, { passive: true })
    window.addEventListener('keydown', this.onWindowKeydown)

    this.$nextTick(() => {
      this.initResizeObserver()
    })
  },

  beforeUnmount() {
    this.closeTooltip()

    this.destroySummaryCharts()
    this.cancelScheduledFit()
    this.cancelScheduledChartRender()
    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('keydown', this.onWindowKeydown)

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },

  methods: {
    async fetchLoggerStatus(siteId) {
      try {
        const res = await api.get('/app/site/loggerStatus', {
          params: { siteId }
        })

        this.loggerStatusList = Array.isArray(res?.data?.result)
          ? res.data.result
          : []
      } catch (e) {
        console.error('현장 로거 상태 조회 실패', e)
        this.loggerStatusList = []
      }
    },

    async fetchButtons(siteId) {
      try {
        const res = await api.get('/app/site/step4/detail', {
          params: { siteId }
        })

        const result = res?.data?.result || {}
        const buttonItems = Array.isArray(result?.buttonItems) ? result.buttonItems : []

        this.buttonItems = buttonItems.map((item, index) => {
          const firstImage =
            Array.isArray(item.buttonImageList) && item.buttonImageList.length
              ? item.buttonImageList[0]
              : null

          return {
            ...item,
            slotNo: item.slotNo ?? index + 1,
            buttonNo: item.buttonNo ?? index + 1,
            topText: item.topText ?? '',
            bottomText: item.bottomText ?? '',
            bgColor:
              item.bgColor ??
              item.topBgColor ??
              item.bottomBgColor ??
              '#f3f4f6',
            textColor:
              item.textColor ??
              item.topTextColor ??
              item.bottomTextColor ??
              '#111827',
            imageUrl: this.resolveButtonImageUrl(firstImage)
          }
        })
      } catch (e) {
        console.error('현장 버튼 조회 실패', e)
        this.buttonItems = []
      }
    },

    safeRenderSummaryCharts() {
      this.cancelScheduledChartRender()

      this.chartRafId = requestAnimationFrame(() => {
        this.chartRafId = null
        this.renderSummaryCharts()
      })
    },

    cancelScheduledChartRender() {
      if (this.chartRafId) {
        cancelAnimationFrame(this.chartRafId)
        this.chartRafId = null
      }
    },

    renderSummaryCharts() {
      this.destroySummaryCharts()
      this.renderInstallChart()
      this.renderCommunicationChart()
    },

    destroySummaryCharts() {
      if (this.installChart) {
        this.installChart.destroy()
        this.installChart = null
      }

      if (this.communicationChart) {
        this.communicationChart.destroy()
        this.communicationChart = null
      }
    },

    getCommonDoughnutOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        animation: {
          duration: 300
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label(context) {
                const label = context.label || ''
                const value = context.raw || 0
                return `${label}: ${value}`
              }
            }
          }
        }
      }
    },

    createChart(canvasRef, labels, values, colors) {
      const canvas = this.$refs[canvasRef]
      if (!canvas) return null

      const ctx = canvas.getContext('2d')
      if (!ctx) return null

      const hasValue = Array.isArray(values) && values.some(v => Number(v) > 0)
      const safeValues = hasValue ? values : [1]
      const safeLabels = hasValue ? labels : ['데이터 없음']
      const safeColors = hasValue ? colors : ['#d1d5db']

      return new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: safeLabels,
          datasets: [
            {
              data: safeValues,
              backgroundColor: safeColors,
              borderColor: '#ffffff',
              borderWidth: 2,
              hoverOffset: 4
            }
          ]
        },
        options: this.getCommonDoughnutOptions()
      })
    },

    renderInstallChart() {
      this.installChart = this.createChart(
        'installChartCanvas',
        this.installLegend.map(item => item.label),
        this.installLegend.map(item => item.value),
        this.installLegend.map(item => item.color)
      )
    },

    renderCommunicationChart() {
      this.communicationChart = this.createChart(
        'communicationChartCanvas',
        this.communicationLegend.map(item => item.label),
        this.communicationLegend.map(item => item.value),
        this.communicationLegend.map(item => item.color)
      )
    },

    resolveButtonImageUrl(image) {
      if (!image) return ''
      if (image.fileUrl) return image.fileUrl
      if (image.previewUrl) return image.previewUrl
      if (image.attachFileNo) return `/app/file/image/${image.attachFileNo}`
      if (image.id) return `/app/file/image/${image.id}`
      return ''
    },

    onImageLoad(event) {
      const img = event?.target
      if (!img) return

      this.imageNaturalWidth = img.naturalWidth || 0
      this.imageNaturalHeight = img.naturalHeight || 0
      this.safeUpdatePlanFit()
    },

    onWindowResize() {
      this.safeUpdatePlanFit()
    },

    safeUpdatePlanFit() {
      this.cancelScheduledFit()

      this.fitRafId = requestAnimationFrame(() => {
        this.fitRafId = null
        this.updatePlanFit()
      })
    },

    cancelScheduledFit() {
      if (this.fitRafId) {
        cancelAnimationFrame(this.fitRafId)
        this.fitRafId = null
      }
    },

    updatePlanFit() {
      const viewport = this.$refs.planViewport

      if (!viewport || !this.imageNaturalWidth || !this.imageNaturalHeight) {
        return
      }

      const viewportWidth = Math.max(viewport.clientWidth - 24, 0)

      if (!viewportWidth) return

      const imageRatio = this.imageNaturalWidth / this.imageNaturalHeight

      const renderWidth = viewportWidth
      const renderHeight = renderWidth / imageRatio

      const nextWidth = Math.floor(renderWidth)
      const nextHeight = Math.floor(renderHeight)

      this.planRenderWidth = nextWidth
      this.planRenderHeight = nextHeight
    },

    getLoggerKey(logger) {
      return logger.sensorLoggerSiteId || logger.sensorLoggerId || logger.name
    },

    openTooltip(logger) {
      this.cancelCloseTooltip()
      this.activeTooltipId = this.getLoggerKey(logger)
    },

    scheduleCloseTooltip() {
      this.cancelCloseTooltip()

      this.tooltipCloseTimer = setTimeout(() => {
        this.activeTooltipId = null
        this.tooltipCloseTimer = null
      }, 50)
    },

    cancelCloseTooltip() {
      if (this.tooltipCloseTimer) {
        clearTimeout(this.tooltipCloseTimer)
        this.tooltipCloseTimer = null
      }
    },

    closeTooltip() {
      this.cancelCloseTooltip()
      this.activeTooltipId = null
    },

    normalizeServerPoint(value, fullSize) {
      const num = Number(value ?? 0)

      if (!Number.isFinite(num)) return 0

      if (num >= 0 && num <= 100) {
        return (num / 100) * fullSize
      }

      return num
    },

    getLoggerStyle(logger) {
      if (!this.imageNaturalWidth || !this.imageNaturalHeight) {
        return { display: 'none' }
      }

      const x = this.normalizeServerPoint(logger.positionX, this.imageNaturalWidth)
      const y = this.normalizeServerPoint(logger.positionY, this.imageNaturalHeight)

      const yOffsetPx = -10

      return {
        left: `${(x / this.imageNaturalWidth) * 100}%`,
        top: `calc(${(y / this.imageNaturalHeight) * 100}% + ${yOffsetPx}px)`
      }
    },

    getLoggerStatus(logger) {
      return logger?.eventType || '정상'
    },

    getLoggerIcon(logger) {
      return this.iconMap[this.getLoggerStatus(logger)] || this.iconMap['정상']
    },

    hasMeaningfulEvent(logger) {
      const status = this.getLoggerStatus(logger)
      return status === '경보' || status === '경고' || status === '감지'
    },

    getTooltipClass(logger) {
      const status = this.getLoggerStatus(logger)

      if (status === '경보') return 'is-dangerous'
      if (status === '경고') return 'is-warning'
      if (status === '감지') return 'is-detect'
      return 'is-normal'
    },

    getButtonTextStyle(btn) {
      return {
        backgroundColor: btn?.bgColor || '#f3f4f6',
        color: btn?.textColor || '#111827'
      }
    },

    isSelectedButton(btn) {
      if (!this.isButtonModalOpen) return false
      if (!this.activeButton) return false
      return Number(btn?.buttonNo) === Number(this.activeButton?.buttonNo)
    },

    openButtonModal(btn) {
      const targetIndex = this.visibleButtons.findIndex(
        item => Number(item.buttonNo) === Number(btn.buttonNo)
      )

      this.activeButtonIndex = targetIndex >= 0 ? targetIndex : 0
      this.isButtonModalOpen = true
      this.closeTooltip()
      this.$emit('button-click', btn)
    },

    closeButtonModal(restoreFit = true) {
      this.isButtonModalOpen = false
      if (restoreFit) {
        this.safeUpdatePlanFit()
      }
    },

    prevButton() {
      if (!this.visibleButtons.length) return

      this.activeButtonIndex =
        this.activeButtonIndex === 0
          ? this.visibleButtons.length - 1
          : this.activeButtonIndex - 1
    },

    nextButton() {
      if (!this.visibleButtons.length) return

      this.activeButtonIndex =
        this.activeButtonIndex === this.visibleButtons.length - 1
          ? 0
          : this.activeButtonIndex + 1
    },

    goToButton(index) {
      this.activeButtonIndex = index
    },

    onWindowKeydown(event) {
      if (!this.isButtonModalOpen) return

      if (event.key === 'Escape') {
        this.closeButtonModal()
      } else if (event.key === 'ArrowLeft') {
        this.prevButton()
      } else if (event.key === 'ArrowRight') {
        this.nextButton()
      }
    },

    formatValue(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '-'
      return num.toFixed(4)
    },

    formatDateTime(value) {
      if (!value) return '-'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value

      const yyyy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      const ss = String(date.getSeconds()).padStart(2, '0')

      return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`
    },

    initResizeObserver() {
      const viewport = this.$refs.planViewport
      if (!viewport) return

      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.safeUpdatePlanFit()
      })

      this.resizeObserver.observe(viewport)
    },

    async fetchOperationSummary(siteId) {
      try {
        const response = await api.get('/app/sensorLogger/summary', {
          params: { siteId }
        })

        const result = response?.data?.result || {}

        this.installLegend = this.normalizeLegendItems(
          result?.installSummary?.items,
          [
            { label: '데이터 없음', value: 0, color: '#d1d5db' }
          ]
        )

        this.communicationLegend = this.normalizeLegendItems(
          result?.communicationSummary?.items,
          [
            { label: '정상', value: 0, color: '#4a78c9' },
            { label: '통신 두절', value: 0, color: '#f28c28' }
          ]
        )
      } catch (error) {
        console.error('현장 장비 요약 조회 실패', error)

        this.installLegend = [
          { label: '데이터 없음', value: 0, color: '#d1d5db' }
        ]

        this.communicationLegend = [
          { label: '정상', value: 0, color: '#4a78c9' },
          { label: '통신 두절', value: 0, color: '#f28c28' }
        ]
      }
    },

    normalizeLegendItems(items, fallbackItems) {
      const list = Array.isArray(items) ? items : []

      const normalized = list.map(item => ({
        label: item.label || '-',
        value: Number(item.value || 0),
        color: item.color || '#94a3b8'
      }))

      return normalized.length ? normalized : fallbackItems
    },
  }
}
</script>

<style scoped>
.site-plan-viewer {
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #eef2f7;
  border-radius: 14px;
  overflow-y: auto;
  overflow-x: hidden;
}

.site-plan-shell {
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  box-sizing: border-box;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.compact-card {
  flex: 0 0 auto;
}

.plan-card {
  flex: 0 0 auto;
  min-height: auto;
  display: flex;
  flex-direction: column;
}

.section-head {
  min-height: 56px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-sizing: border-box;
  border-bottom: 1px solid #edf2f7;
}

.section-head-text {
  min-width: 0;
}

.section-title {
  font-size: 15px;
  font-weight: 800;
  color: #111827;
  line-height: 1.2;
}

.section-subtitle {
  margin-top: 3px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

/* 상단 버튼 영역 */
.button-gallery-section {
  padding: 10px 14px 14px;
}

.button-gallery-list {
  display: flex;
  align-items: stretch;
  gap: 8px;
  flex-wrap: wrap;
}

.button-gallery-card {
  width: 92px;
  min-width: 92px;
  height: 108px;
  padding: 0;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  box-shadow: 0 5px 12px rgba(15, 23, 42, 0.08);
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease,
    border-color 0.16s ease;
}

.button-gallery-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.12);
  border-color: rgba(37, 99, 235, 0.24);
}

.button-gallery-card.active {
  border-color: #2563eb;
  box-shadow:
    0 10px 20px rgba(37, 99, 235, 0.14),
    0 0 0 3px rgba(37, 99, 235, 0.08);
}

.button-gallery-top,
.button-gallery-bottom {
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px 5px;
  text-align: center;
  box-sizing: border-box;
}

.button-gallery-bottom {
  margin-top: auto;
}

.button-gallery-text {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  font-size: 10px;
  font-weight: 800;
  line-height: 1.2;
}

.button-gallery-image-wrap {
  flex: 1;
  min-height: 0;
  padding: 4px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-gallery-image {
  width: 100%;
  height: 100%;
  max-height: 56px;
  object-fit: cover;
  border-radius: 8px;
  display: block;
  background: #f8fafc;
}

.button-gallery-image-empty,
.button-gallery-empty {
  width: 100%;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  background: #f8fafc;
  color: #94a3b8;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 8px;
  box-sizing: border-box;
}

.button-gallery-image-empty {
  height: 56px;
}

.button-gallery-empty {
  min-height: 72px;
}

/* 현장 도면 */
.viewer-stage-section {
  padding: 12px;
  box-sizing: border-box;
}

.image-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px;
  box-sizing: border-box;
}

.plan-stage {
  position: relative;
  flex-shrink: 0;
  line-height: 0;
}

.plan-image {
  display: block;
  width: 100%;
  height: 100%;
  max-width: none;
  object-fit: contain;
}

.placeholder {
  width: 100%;
  height: 100%;
  min-height: 320px;
  border-radius: 16px;
  border: 1px dashed #cbd5e1;
  background: rgba(255, 255, 255, 0.9);
  color: #64748b;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 로거 마커 */
.logger-marker {
  position: absolute;
  transform: translate(-50%, -100%);
  z-index: 20;
  pointer-events: auto;
}

.logger-marker.active {
  z-index: 200;
}

.logger-icon-wrap {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
}

.logger-icon {
  width: 34px;
  height: 34px;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.2));
  position: relative;
  z-index: 1;
}

.logger-tooltip {
  position: absolute;
  left: 28px;
  top: -6px;
  min-width: 240px;
  max-width: 300px;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  line-height: 1.45;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.14);
  pointer-events: auto;
  z-index: 300;
  white-space: normal;
  border-radius: 10px;
}

.logger-tooltip.is-dangerous {
  border-color: #ef4444;
}

.logger-tooltip.is-warning {
  border-color: #f59e0b;
}

.logger-tooltip.is-detect {
  border-color: #8b5cf6;
}

.logger-tooltip.is-normal {
  border-color: #cbd5e1;
}

.tooltip-row + .tooltip-row {
  margin-top: 4px;
}

.tooltip-label {
  font-weight: 700;
  margin-right: 4px;
}

.tooltip-value {
  color: #111827;
  word-break: break-word;
}

.tooltip-search-guide {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 6px;
  padding-left: 8px;
  padding-right: 8px;
  padding-bottom: 8px;
}

.tooltip-guide-title {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 4px;
}

.tooltip-guide-line {
  font-size: 12px;
  color: #111827;
  line-height: 1.45;
}

/* 하단 요약 차트 */
.plan-summary-section {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 12px;
  box-sizing: border-box;
}

.summary-chart-card {
  border: 1px solid #edf2f7;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
  padding: 14px 16px;
}

.summary-chart-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.summary-chart-index {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #3b82f6;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.summary-chart-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.summary-chart-body {
  display: flex;
  align-items: center;
  gap: 16px;
}

.summary-chart-canvas-wrap {
  width: 140px;
  flex-shrink: 0;
}

.summary-chart-canvas {
  position: relative;
  width: 130px;
  height: 130px;
}

.summary-chart-meta {
  flex: 1;
  min-width: 0;
}

.summary-meta-total {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #374151;
}

.summary-legend-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.summary-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
  color: #4b5563;
}

.summary-legend-color {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.summary-legend-label {
  min-width: 72px;
}

.summary-legend-value {
  color: #111827;
  font-weight: 600;
}

/* 버튼 이미지 팝업 */
.button-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(3px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  box-sizing: border-box;
}

.button-modal-panel {
  width: min(1180px, 100%);
  max-height: calc(100vh - 36px);
  border-radius: 20px;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.08), transparent 24%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.28);
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: 12px;
  padding: 16px;
  box-sizing: border-box;
  overflow: hidden;
}

.button-modal-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.button-modal-meta {
  min-width: 0;
}

.button-modal-title {
  font-size: 18px;
  font-weight: 800;
  color: #111827;
  line-height: 1.2;
}

.button-modal-count {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}

.button-modal-close {
  height: 40px;
  padding: 0 16px;
  border: 1px solid #dbe3ef;
  border-radius: 12px;
  background: #ffffff;
  color: #1f2937;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.05);
  transition:
    background-color 0.16s ease,
    border-color 0.16s ease,
    transform 0.16s ease;
}

.button-modal-close:hover {
  background: #f8fafc;
  border-color: #bfd2f6;
  transform: translateY(-1px);
}

.button-modal-body {
  min-height: 0;
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) 64px;
  gap: 14px;
  align-items: center;
}

.button-modal-nav {
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.2);
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease;
  justify-self: center;
  text-align: center;
}

.button-modal-nav:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 24px rgba(37, 99, 235, 0.26);
}

.button-modal-content {
  width: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.button-modal-card {
  width: min(100%, 880px);
  min-width: 720px;
  min-height: 520px;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #ffffff;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.1);
  display: grid;
  grid-template-rows: auto minmax(360px, 1fr) auto;
}

.button-modal-card-top,
.button-modal-card-bottom {
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  text-align: center;
  box-sizing: border-box;
}

.button-modal-card-text {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
}

.button-modal-image-stage {
  min-height: 360px;
  padding: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.button-modal-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 16px;
  display: block;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

/* ✅ 빈 이미지 전용: 카드형 버튼 UI 자체를 버리고 전체 폭 안내 패널로 표시 */
.button-modal-empty-panel {
  width: min(100%, 880px);
  min-width: 720px;
  min-height: 520px;
  border-radius: 20px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.1);
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;
}

.button-modal-empty-header {
  padding: 22px 24px 12px;
  border-bottom: 1px solid #eef2f7;
  box-sizing: border-box;
}

.button-modal-empty-title-wrap {
  text-align: left;
}

.button-modal-empty-title-main {
  font-size: 20px;
  font-weight: 800;
  color: #111827;
  line-height: 1.25;
}

.button-modal-empty-title-sub {
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
}

.button-modal-empty-body {
  min-height: 360px;
  padding: 28px 24px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
}

.button-modal-empty-icon {
  width: 84px;
  height: 84px;
  border-radius: 999px;
  background: #eef4ff;
  color: #7c97c9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  margin-bottom: 18px;
}

.button-modal-empty-message {
  font-size: 20px;
  font-weight: 800;
  color: #334155;
  margin-bottom: 8px;
}

.button-modal-empty-desc {
  font-size: 14px;
  line-height: 1.6;
  color: #94a3b8;
  margin-bottom: 26px;
}

.button-modal-empty-meta {
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.button-modal-meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.meta-label {
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
}

.meta-value.chip {
  min-width: 180px;
  max-width: 280px;
  min-height: 38px;
  padding: 8px 14px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  box-sizing: border-box;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 900px) {
  .button-modal-card,
  .button-modal-empty-panel {
    width: 100%;
    min-width: 0;
    min-height: 420px;
  }

  .button-modal-image-stage,
  .button-modal-empty-body {
    min-height: 260px;
  }

  .button-modal-empty-header {
    padding: 18px 18px 10px;
  }

  .button-modal-empty-body {
    padding: 20px 18px 24px;
  }

  .button-modal-meta-row {
    flex-direction: column;
    align-items: stretch;
  }

  .meta-value.chip {
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }
}

.button-modal-indicators {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.button-modal-dot {
  width: 10px;
  height: 10px;
  border: none;
  border-radius: 999px;
  background: #d1d5db;
  cursor: pointer;
  padding: 0;
}

.button-modal-dot.active {
  background: #2563eb;
}

.img-placeholder {
  width: 100%;
  height: 100%;
  max-height: 56px;
  background: #f3f4f6;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:8px;
  font-size:11px;
  color:#94a3b8;
}

@media (max-width: 1200px) {
  .button-gallery-card {
    width: 88px;
    min-width: 88px;
    height: 102px;
  }

  .button-gallery-image,
  .button-gallery-image-empty {
    max-height: 52px;
    height: 52px;
  }

  .logger-icon {
    width: 30px;
    height: 30px;
  }

  .plan-summary-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .site-plan-shell {
    padding: 10px;
    gap: 10px;
  }

  .button-gallery-list {
    gap: 8px;
  }

  .summary-chart-body {
    flex-direction: column;
    align-items: flex-start;
  }

  .button-modal-panel {
    padding: 12px;
  }

  .button-modal-topbar {
    flex-direction: column;
    align-items: stretch;
  }

  .button-modal-body {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .button-modal-nav {
    width: 50px;
    height: 50px;
  }

  .button-modal-card {
    width: 100%;
  }
}

.button-modal-body.single-mode {
  grid-template-columns: minmax(0, 1fr);
}

.button-modal-single-panel {
  width: min(100%, 880px);
  min-width: 720px;
  min-height: 520px;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #ffffff;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.1);
  display: grid;
  grid-template-rows: auto minmax(360px, 1fr) auto;
}

.button-modal-single-top,
.button-modal-single-bottom {
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  text-align: center;
  box-sizing: border-box;
}

.button-modal-single-text {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
}

.button-modal-single-image-stage {
  min-height: 360px;
  padding: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.button-modal-single-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 16px;
  display: block;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

.button-modal-empty-wide-panel {
  width: min(100%, 880px);
  min-width: 720px;
  min-height: 520px;
  border-radius: 20px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.1);
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;
}

.button-modal-empty-wide-header {
  padding: 22px 24px 12px;
  border-bottom: 1px solid #eef2f7;
  box-sizing: border-box;
  text-align: left;
}

.button-modal-empty-wide-title {
  font-size: 20px;
  font-weight: 800;
  color: #111827;
  line-height: 1.25;
}

.button-modal-empty-wide-subtitle {
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
}

.button-modal-empty-wide-body {
  min-height: 360px;
  padding: 28px 24px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
}

.button-modal-empty-wide-icon {
  width: 84px;
  height: 84px;
  border-radius: 999px;
  background: #eef4ff;
  color: #7c97c9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  margin-bottom: 18px;
}

.button-modal-empty-wide-message {
  font-size: 20px;
  font-weight: 800;
  color: #334155;
  margin-bottom: 8px;
}

.button-modal-empty-wide-desc {
  font-size: 14px;
  line-height: 1.6;
  color: #94a3b8;
  margin-bottom: 26px;
}

.button-modal-empty-wide-meta {
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.button-modal-empty-wide-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

@media (max-width: 900px) {
  .button-modal-single-panel,
  .button-modal-empty-wide-panel {
    width: 100%;
    min-width: 0;
    min-height: 420px;
  }

  .button-modal-single-image-stage,
  .button-modal-empty-wide-body {
    min-height: 260px;
  }

  .button-modal-empty-wide-row {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>